import { Block, ContentFitSnapshot } from '../types';
import {
  getBlockDimensions,
  getDecisionVisibleCharCap,
  getProcessLikeVisibleCharCap,
  getStartEndVisibleCharCap,
} from './blockUtils';

export const MIN_FIT_BLOCK_HEIGHT = 30;

/** Largeur utile relative dans le losange (bande centrale). */
const DECISION_USABLE_WIDTH_RATIO = 0.5;

type FitEligibleBlock = Block & { type: 'process' | 'start' | 'end' | 'decision' };

let measureCanvas: HTMLCanvasElement | null = null;

function isFitEligibleBlock(block: Block): block is FitEligibleBlock {
  return (
    block.type === 'process' ||
    block.type === 'start' ||
    block.type === 'end' ||
    block.type === 'decision'
  );
}

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (typeof document === 'undefined') return null;
  if (!measureCanvas) {
    measureCanvas = document.createElement('canvas');
  }
  return measureCanvas.getContext('2d');
}

function getDefaultPadding(block: FitEligibleBlock): number {
  if (block.textStyle?.padding != null) {
    return block.textStyle.padding;
  }
  if (block.type === 'process') return 8;
  if (block.type === 'start' || block.type === 'end') return 10;
  return 0;
}

function getDefaultFontSize(block: FitEligibleBlock): number {
  if (block.textStyle?.fontSize) return block.textStyle.fontSize;
  return block.type === 'decision' ? 12 : 14;
}

function getDisplayText(block: FitEligibleBlock): string {
  const text = block.text || '';
  const transform = block.textStyle?.textTransform || 'none';

  if (transform === 'uppercase') return text.toUpperCase();
  if (transform === 'lowercase') return text.toLowerCase();
  if (transform === 'capitalize') {
    return text.replace(/\b\w/g, (char) => char.toUpperCase());
  }
  return text;
}

function resolveFontFamily(fontFamily?: string): string {
  if (!fontFamily || fontFamily === 'inherit') {
    return 'Arial, Helvetica, sans-serif';
  }
  return fontFamily;
}

function estimateTextWidth(text: string, fontSize: number, fontWeight?: string | number): number {
  const isBold = fontWeight === 'bold' || fontWeight === 700;
  return text.length * fontSize * (isBold ? 0.62 : 0.55);
}

function measureTextLine(text: string, block: FitEligibleBlock): number {
  if (!text) return 0;

  const fontSize = getDefaultFontSize(block);
  const fontWeight = block.textStyle?.fontWeight || 'normal';
  const ctx = getMeasureContext();

  if (!ctx) {
    return estimateTextWidth(text, fontSize, fontWeight);
  }

  const fontStyle = block.textStyle?.fontStyle || 'normal';
  const fontFamily = resolveFontFamily(block.textStyle?.fontFamily);
  ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
  return ctx.measureText(text).width;
}

function wrapTextToLines(block: FitEligibleBlock, maxWidth: number): string[] {
  const text = getDisplayText(block);
  if (!text) return [''];
  if (maxWidth <= 0) return [text];

  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [''];

  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const candidate = currentLine ? `${currentLine} ${word}` : word;

    if (measureTextLine(candidate, block) <= maxWidth) {
      currentLine = candidate;
      continue;
    }

    if (currentLine) {
      lines.push(currentLine);
      currentLine = '';
    }

    if (measureTextLine(word, block) > maxWidth) {
      let chunk = '';
      for (const char of word) {
        const nextChunk = `${chunk}${char}`;
        if (measureTextLine(nextChunk, block) > maxWidth && chunk) {
          lines.push(chunk);
          chunk = char;
        } else {
          chunk = nextChunk;
        }
      }
      currentLine = chunk;
    } else {
      currentLine = word;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.length ? lines : [''];
}

function getLineHeight(block: FitEligibleBlock): number {
  const fontSize = getDefaultFontSize(block);
  const lineHeightValue = block.textStyle?.lineHeight;
  if (typeof lineHeightValue === 'number' && lineHeightValue > 0) {
    return lineHeightValue;
  }
  return fontSize * 1.25;
}

function availableTextWidthFor(block: FitEligibleBlock, boxWidth: number): number {
  if (block.type === 'decision') {
    return Math.max(24, boxWidth * DECISION_USABLE_WIDTH_RATIO);
  }
  const padding = getDefaultPadding(block);
  const border = 2;
  return Math.max(24, boxWidth - padding * 2 - border);
}

/** Dimensions « initiales » (custom ou défaut type) servant de base au ratio. */
function getBaseBox(block: FitEligibleBlock): { width: number; height: number } {
  const typeDefaults = getBlockDimensions({
    ...block,
    width: undefined,
    height: undefined,
  });
  return {
    width: block.width ?? typeDefaults.width,
    height: block.height ?? typeDefaults.height,
  };
}

function neededHeightForBoxWidth(block: FitEligibleBlock, boxWidth: number): number {
  const padding = getDefaultPadding(block);
  const lineHeight = getLineHeight(block);
  const border = 2;
  const lines = wrapTextToLines(block, availableTextWidthFor(block, boxWidth));
  return Math.ceil(lines.length * lineHeight + padding * 2 + border + 4);
}

export function isContentFitActive(block: Block): block is FitEligibleBlock {
  return isFitEligibleBlock(block) && Boolean(block.contentFitSnapshot);
}

export function hasContentFitApplied(blocks: Block[]): boolean {
  return blocks.some((block) => isContentFitActive(block));
}

function getVisibleCharCapForFit(block: FitEligibleBlock): number {
  if (block.type === 'decision') {
    return getDecisionVisibleCharCap(block.textStyle?.fontSize, block);
  }
  if (block.type === 'start' || block.type === 'end') {
    return getStartEndVisibleCharCap(block.textStyle?.fontSize, block);
  }
  return getProcessLikeVisibleCharCap(block.textStyle?.fontSize, block);
}

/** True si le texte affiché serait tronqué (même règle que le rendu). */
export function isBlockTextTruncated(block: Block): boolean {
  if (!isFitEligibleBlock(block)) return false;
  const text = block.text || '';
  if (!text) return false;
  return text.length > getVisibleCharCapForFit(block);
}

/** Styles appliqués uniquement en mode ajusté (multiligne HTML). */
export function applyContentFitTextStyles(element: HTMLElement): void {
  element.style.whiteSpace = 'normal';
  element.style.wordBreak = 'break-word';
  element.style.overflowWrap = 'break-word';
}

/** Lignes wrappées pour le mode content fit (process HTML ou décision SVG). */
export function getContentFitTextLines(block: Block): string[] {
  if (!isFitEligibleBlock(block)) return [block.text || ''];
  const dims = getBlockDimensions(block);
  return wrapTextToLines(block, availableTextWidthFor(block, dims.width));
}

/**
 * Agrandit la boîte en conservant le ratio width/height initial
 * jusqu'à ce que le texte wrappé tienne.
 */
export function computeFitToContentDimensions(block: FitEligibleBlock): {
  width: number;
  height: number;
} {
  const { width: baseWidth, height: baseHeight } = getBaseBox(block);
  const aspect = baseWidth / Math.max(1, baseHeight);

  let scale = 1;
  const maxScale = 8;
  const step = 0.05;

  while (scale <= maxScale) {
    const height = Math.max(baseHeight, Math.round(baseHeight * scale));
    const width = Math.max(baseWidth, Math.round(height * aspect));
    if (neededHeightForBoxWidth(block, width) <= height) {
      return { width, height };
    }
    scale += step;
  }

  const fallbackHeight = Math.max(
    baseHeight,
    neededHeightForBoxWidth(block, Math.round(baseHeight * maxScale * aspect))
  );
  return {
    height: fallbackHeight,
    width: Math.max(baseWidth, Math.round(fallbackHeight * aspect)),
  };
}

export function computeFitToContentHeight(block: FitEligibleBlock): number {
  return computeFitToContentDimensions(block).height;
}

function createSnapshot(block: FitEligibleBlock): ContentFitSnapshot {
  return (
    block.contentFitSnapshot ?? {
      height: block.height ?? null,
      width: block.width ?? null,
    }
  );
}

export function fitBlocksToContent(blocks: Block[]): Block[] {
  return blocks.map((block) => {
    if (!isFitEligibleBlock(block)) return block;
    if (!isBlockTextTruncated(block)) return block;

    const { width, height } = computeFitToContentDimensions(block);

    return {
      ...block,
      width,
      height,
      contentFitSnapshot: createSnapshot(block),
    };
  });
}

/** Restaure exactement l'état d'avant le premier ajustement. */
export function resetBlocksContentFit(blocks: Block[]): Block[] {
  return blocks.map((block) => {
    if (!isFitEligibleBlock(block) || !block.contentFitSnapshot) return block;

    const { contentFitSnapshot, ...rest } = block;
    const restored: Block = { ...rest };

    if (contentFitSnapshot.height === null) {
      delete restored.height;
    } else {
      restored.height = contentFitSnapshot.height;
    }

    if (contentFitSnapshot.width === null) {
      delete restored.width;
    } else {
      restored.width = contentFitSnapshot.width;
    }

    return restored;
  });
}

export function toggleBlocksContentFit(blocks: Block[]): Block[] {
  return hasContentFitApplied(blocks)
    ? resetBlocksContentFit(blocks)
    : fitBlocksToContent(blocks);
}
